import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Bookmark, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function TagManagerDialog({ open, onOpenChange, onChange }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // tag being renamed
  const [draft, setDraft] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/my-tags");
      setTags(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const startRename = (t) => { setEditing(t.tag); setDraft(t.tag); };
  const cancelRename = () => { setEditing(null); setDraft(""); };

  const saveRename = async (oldTag) => {
    const next = draft.trim();
    if (!next || next === oldTag) { cancelRename(); return; }
    try {
      await api.put(`/my-tags/${encodeURIComponent(oldTag)}`, { new_tag: next });
      toast.success(`Renamed to "${next}"`);
      cancelRename();
      load();
      onChange?.();
    } catch (e) {
      toast.error("Couldn't rename");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete tag "${t.tag}" from all your threads? (${t.count} thread${t.count === 1 ? "" : "s"})`)) return;
    try {
      await api.delete(`/my-tags/${encodeURIComponent(t.tag)}`);
      toast.success(`Deleted "${t.tag}"`);
      load();
      onChange?.();
    } catch (e) {
      toast.error("Couldn't delete");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-[95vw] max-h-[95vh] overflow-y-auto" data-testid="tag-manager-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Bookmark className="w-4 h-4 text-[#0033CC]" />
            Your tags
          </DialogTitle>
          <DialogDescription>
            These tags are private — only you see them. Rename or delete here applies across all your tagged threads.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-80 overflow-y-auto -mx-2 px-2">
          {loading && <p className="text-sm text-zinc-500 py-3">Loading…</p>}
          {!loading && tags.length === 0 && (
            <p className="text-sm text-zinc-500 py-6 text-center italic">
              You haven't created any personal tags yet. Open any thread and add one in the "Your tags" row.
            </p>
          )}
          <ul className="space-y-1">
            {tags.map((t) => (
              <li
                key={t.tag}
                className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-zinc-50 group"
                data-testid={`tag-row-${t.tag}`}
              >
                <Bookmark className="w-3.5 h-3.5 text-[#0033CC] shrink-0" strokeWidth={2} fill="currentColor" />
                {editing === t.tag ? (
                  <>
                    <Input
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveRename(t.tag);
                        if (e.key === "Escape") cancelRename();
                      }}
                      autoFocus
                      className="h-7 flex-1 text-sm"
                      data-testid={`tag-rename-input-${t.tag}`}
                    />
                    <button onClick={() => saveRename(t.tag)} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" data-testid={`tag-save-${t.tag}`}>
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={cancelRename} className="p-1 text-zinc-500 hover:bg-zinc-100 rounded">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm">{t.tag}</span>
                    <span className="text-xs text-zinc-500 font-mono">{t.count}</span>
                    <button
                      onClick={() => startRename(t)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-zinc-500 hover:text-[#0033CC] hover:bg-zinc-100 rounded transition-opacity"
                      data-testid={`tag-rename-${t.tag}`}
                      title="Rename"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => remove(t)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-zinc-500 hover:text-red-600 hover:bg-zinc-100 rounded transition-opacity"
                      data-testid={`tag-delete-${t.tag}`}
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
